import {
  useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';

/** Distância entre o popover e o alvo (`top: calc(100% + 6px)`, .dc.html:179 e 192). */
export const PERIOD_POPOVER_OFFSET: [number, number] = [0, 6];

/** Por que um popover do período fechou sem escolher nem aplicar nada. */
export type PopoverDismissReason = 'escape' | 'outside';

/**
 * Fechamento dos popovers do período (referência e intervalo, #1996) sem escolher nada.
 *
 * - Esc: `keydown` no documento enquanto o popover está aberto, para valer com o foco em qualquer
 *   lugar. Pula o Esc que outro controle já tratou (`defaultPrevented`): com o calendário do
 *   DatePicker aberto, o 1º Esc fecha só o calendário e o 2º fecha o popover.
 * - Clique fora: o `onHide` devolvido, para o `rootClose` do `Overlay`. O `rootClose` também
 *   chama o `onHide` no `keyup` do Esc, que é ignorado aqui: ele não sabe se o calendário consumiu
 *   a tecla.
 *
 * @param show - Popover aberto.
 * @param onDismiss - Recebe o motivo; o mais recente é sempre o chamado.
 * @returns O `onHide` do `Overlay`.
 */
export default function usePopoverDismiss(
  show: boolean,
  onDismiss: (reason: PopoverDismissReason) => void,
): (event: Event) => void {
  const latest = useRef(onDismiss);
  useLayoutEffect(() => {
    latest.current = onDismiss;
  });

  useEffect(() => {
    if (!show) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) latest.current('escape');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show]);

  return useCallback((event: Event) => {
    if (event.type === 'keyup') return;
    latest.current('outside');
  }, []);
}
